import {environment} from "../../../../environments/environment";
import {HttpClient} from "@angular/common/http";
import {Injectable, OnDestroy} from "@angular/core";
import {
  FraudActivityEnvironment,
  FraudActivityTracker,
  FraudActivityTrackerFactory,
  Platform
} from "@zoominfo/fraud-js-sdk";
import {FeatureFlagService} from "../feature-flag";
import {Flag} from "../feature-flag/constants/flag.enum";
import { v4 as uuidv4 } from 'uuid';
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";

export interface FraudFlow {
  type: string;
  name: string;
}

@Injectable({ providedIn: 'root' })
export class FraudDetectionService implements OnDestroy {
  private readonly fraudClient: FraudActivityTracker;
  private isNewPlatformFraudServiceEnabled = false;
  private readonly destroy$ = new Subject<void>();

  private readonly loginValidationFlow: FraudFlow = {
    name: 'platform_login',
    type: 'login',
  };

  constructor(private readonly featureFlagService: FeatureFlagService,
              protected _http: HttpClient) {
    this.fraudClient = FraudActivityTrackerFactory.getInstance({
      environment: environment.production ? FraudActivityEnvironment.PRODUCTION : FraudActivityEnvironment.SANDBOX,
      platform: Platform.ZOOMINFO
    });
    this.loadServiceTargetFlag();
  }

  private loadServiceTargetFlag(): void {
    this.featureFlagService
      .observe(Flag.LD_USE_FRAUD_PLATFORM_SERVICE)
      .pipe(takeUntil(this.destroy$))
      .subscribe((flagValue: boolean) => {
        this.isNewPlatformFraudServiceEnabled = flagValue;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public async createSession(userId: string): Promise<string> {
    if (!this.isNewPlatformFraudServiceEnabled) {
      console.warn("Platform fraud service is disabled. Returning null session key.");
      return Promise.resolve(null);
    }
    const activityParameters = {
      customerId: userId ?? uuidv4(),
      origin: "login",
      isNewPlatformFraudServiceEnabled: this.isNewPlatformFraudServiceEnabled,
      validationFlow: this.loginValidationFlow.name,
      applicationId: "login",
      sourceId: "login"
    }
    try {
      const sessionKey = await this.fraudClient.startActivitySession(activityParameters);
      console.log("startActivitySession returned sessionKey:", sessionKey);
      return sessionKey;
    } catch (error) {
      console.error("startActivitySession threw error:", error);
      throw error;
    }
  }

  public sendLoginDetails(sessionKey: string, accesstoken: string, loginType: string): void {
    if (!this.isNewPlatformFraudServiceEnabled) {
      console.warn("Platform fraud service is disabled. Skip calling SM");
      return;
    }
    const url = `${environment.sm_fraud_detection_url}`;
    const headers = {
      'application-id': 'login',
      'source-id': 'login',
      'x-ziaccesstoken': accesstoken,
    };
    const body = { loginType, sessionKey };

    this._http.post(url, body, { headers }).subscribe({
      error: (error) => console.error('Error sending login information', error)
    });
  }

}
